/**
 * The navigation icons, as path data from the design.
 *
 * One component and a lookup rather than twenty files: each of these is a
 * single stroked path, and a file per icon would be twenty imports to say what
 * a string says. They are stroked rather than filled so one shape works on both
 * themes without a second copy.
 *
 * `aria-hidden` on every one. The label beside it already says what the link
 * is, and a screen reader announcing "graphic, contacts, link, Contacts" reads
 * the same thing twice.
 */

export type IconName =
  | 'dashboard'
  | 'contacts'
  | 'segments'
  | 'import'
  | 'duplicates'
  | 'campaigns'
  | 'compose'
  | 'templates'
  | 'monitor'
  | 'suppression'
  | 'domains'
  | 'reputation'
  | 'consent'
  | 'settings'
  | 'profile'
  | 'team'
  | 'key'
  | 'admin'
  | 'sun'
  | 'moon'
  | 'search'
  | 'sparkle'
  | 'back'
  | 'undo'
  | 'redo'

const PATHS: Record<IconName, string> = {
  dashboard: 'M4 4h6v8H4zM4 16h6v4H4zM14 12h6v8h-6zM14 4h6v4h-6z',
  contacts:
    'M9 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6M4 20v-1a4 4 0 0 1 4-4h2a4 4 0 0 1 4 4v1M17 8a3 3 0 1 0 0 4M20 20v-1a4 4 0 0 0-3-3.8',
  segments: 'M4 5h16l-6.2 7.5V19l-3.6 2v-8.5z',
  import: 'M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2M7 9l5-5 5 5M12 4v12',
  campaigns: 'M3 7l9 6 9-6M3 6h18v12H3z',
  compose: 'M4 20h4L18.5 9.5a2.8 2.8 0 0 0-4-4L4 16zM13.5 6.5l4 4',
  templates: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  monitor: 'M3 12h4l3 8 4-16 3 8h4',
  suppression: 'M5.6 5.6l12.8 12.8M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18',
  domains:
    'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M3.6 9h16.8M3.6 15h16.8M11.5 3a17 17 0 0 0 0 18M12.5 3a17 17 0 0 1 0 18',
  reputation: 'M4 19h16M4 15l4-5 4 3 5-7',
  consent: 'M12 3l8 3v6c0 5-3.4 8-8 9-4.6-1-8-4-8-9V6zM9 12l2 2 4-4',
  settings:
    'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6M19 12c0-.4 0-.8-.1-1.2l2-1.5-2-3.4-2.3 1a7 7 0 0 0-2-1.2L14.2 3H9.8l-.4 2.7a7 7 0 0 0-2 1.2l-2.3-1-2 3.4 2 1.5A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.4 2.3-1c.6.5 1.3.9 2 1.2l.4 2.7h4.4l.4-2.7c.7-.3 1.4-.7 2-1.2l2.3 1 2-3.4-2-1.5c.1-.4.1-.8.1-1.2z',
  profile: 'M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8M5 20a7 7 0 0 1 14 0',
  key: 'M15.5 4a4.5 4.5 0 1 0-4.3 5.8L4 17v3h3v-2h2v-2h2l2.2-2.2A4.5 4.5 0 0 0 15.5 4M16 8h.01',
  duplicates:
    'M8 4h9v9M4 8h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2M17 4a2 2 0 0 1 2 2',
  team: 'M9 5a3.2 3.2 0 1 0 0 6.4A3.2 3.2 0 0 0 9 5M3 20a6 6 0 0 1 12 0M16.5 11.4a2.6 2.6 0 1 0 0-5.2M17 14.6a5.4 5.4 0 0 1 4 5.4',
  admin: 'M4 21h16M6 21V5l6-2v18M18 21V10l-6-3M9 9h.01M9 13h.01',
  sun: 'M12 4V2M12 22v-2M4 12H2M22 12h-2M6 6L4.6 4.6M19.4 19.4L18 18M6 18l-1.4 1.4M19.4 4.6L18 6M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8',
  moon: 'M21 13a8.2 8.2 0 0 1-10-10 8.5 8.5 0 1 0 10 10z',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14M20 20l-4-4',
  sparkle:
    'M12 3l1.8 4.9L18.7 9.7l-4.9 1.8L12 16.4l-1.8-4.9L5.3 9.7l4.9-1.8zM18 15l.9 2.4 2.4.9-2.4.9L18 21.6l-.9-2.4-2.4-.9 2.4-.9z',
  back: 'M15 6l-6 6 6 6',
  undo: 'M9 7L4 12l5 5M4 12h11a5 5 0 0 1 0 10h-1',
  redo: 'M15 7l5 5-5 5M20 12H9a5 5 0 0 0 0 10h1',
}

export function Icon({ name, size = 16 }: { readonly name: IconName; readonly size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={PATHS[name]} />
    </svg>
  )
}
